import { MemberSearchQuery_members } from '@frontend/app/queries/types/MemberSearchQuery';
import { ISocialAccount } from '@components';
import type { SocialStatsDataAdapter, SocialStatsDataPoint, SocialStatsTab } from '@/types/social-stats';

type MemberWithSocialData = {
  member: MemberSearchQuery_members | null;
  socialData?: ISocialAccount | null;
};

/**
 * Member social stats adapter
 */
export class MemberSocialStatsAdapter implements SocialStatsDataAdapter<MemberWithSocialData> {
  getTabs(): SocialStatsTab[] {
    return [
      { key: 'engagement', heading: 'Engagement', info: 'View Details', lineColor: '#6C77DE' },
      { key: 'reach', heading: 'Reach', info: 'View Details', lineColor: '#82ca9d' },
    ];
  }

  getDefaultTab(): string {
    return 'engagement';
  }

  getEngagementsData(source: MemberWithSocialData): SocialStatsDataPoint[] {
    // For member data, we'll use engagement metrics if available
    const { socialData } = source;
    if (!socialData?.engagement) {
      return [];
    }

    // Since we don't have daily engagement data, create a single point
    // This could be enhanced with actual time series data if available
    return [{
      date: new Date().toISOString().split('T')[0],
      value: socialData.engagement || 0,
    }];
  }

  getReachData(source: MemberWithSocialData): SocialStatsDataPoint[] {
    // For member data, we'll use follower count as reach proxy
    const { socialData } = source;
    if (!socialData?.follower_count) {
      return [];
    }

    return [{
      date: new Date().toISOString().split('T')[0],
      value: socialData.follower_count || 0,
    }];
  }

  getFollowersData(source: MemberWithSocialData): SocialStatsDataPoint[] {
    const { socialData } = source;
    if (!socialData?.follower_history) {
      return [];
    }

    // Convert follower history to chart data points
    return socialData.follower_history.map((entry) => ({
      date: new Date(entry.created_ts * 1000).toISOString().split('T')[0],
      value: entry.count,
    }));
  }

  // Support for tabs
  getDataForTab(source: MemberWithSocialData, tabKey: string): SocialStatsDataPoint[] {
    switch (tabKey) {
      case 'engagement':
        return this.getEngagementsData(source);
      case 'reach':
        return this.getReachData(source);
      default:
        return [];
    }
  }

  getSectionTitle(): string {
    return 'Social Performance';
  }

  getSectionIconBgColor(): string {
    return '#6C77DE';
  }
}

/**
 * Default member social stats adapter instance
 */
export const memberSocialStatsAdapter = new MemberSocialStatsAdapter();
